import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, AlertCircle, Timer, Ban } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { getAverageHandlingTime } from '@/api/analytics/api';
import type { AverageHandlingTimeResponse } from '@/api/analytics/types';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface AverageHandlingTimeChartProps {
    className?: string;
    startDate?: string;
    endDate?: string;
    rubricId?: string;
    keyPath?: string;
    assistantId?: string;
    onError?: (error: string) => void;
}

const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
        return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
};

const chartConfig = {
    total: {
        label: "Total",
        color: "hsl(var(--primary))",
    },
    inbound: {
        label: "Inbound",
        color: "hsl(142, 71%, 45%)", // Professional green
    },
    outbound: {
        label: "Outbound",
        color: "hsl(221, 83%, 53%)", // Professional blue
    },
};

const LoadingSkeleton = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex-1 flex flex-col space-y-4 min-h-0"
    >
        {/* Stats Cards Skeleton - Vertical Layout */}
        <div className="grid grid-cols-3 gap-3 shrink-0">
            {[...Array(3)].map((_, i) => (
                <div key={i} className="rounded-lg border bg-gradient-to-br from-muted/30 to-transparent">
                    <div className="flex flex-col items-center text-center p-3 space-y-2">
                        <Skeleton className="h-8 w-8 rounded-md shrink-0" />
                        <div className="w-full space-y-1.5">
                            <Skeleton className="h-2.5 w-12 mx-auto" />
                            <Skeleton className="h-5 w-16 mx-auto" />
                            <Skeleton className="h-2.5 w-14 mx-auto" />
                        </div>
                    </div>
                </div>
            ))}
        </div>

        {/* Chart Skeleton - Takes remaining space */}
        <div className="flex-1 relative rounded-lg border bg-gradient-to-br from-muted/30 via-background to-background p-4 min-h-0 overflow-hidden">
            {/* Subtle Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
                backgroundSize: '24px 24px'
            }} />
            
            <div className="relative h-full flex items-end justify-around gap-4 px-4 pb-8 pt-8">
                {[65, 85, 75].map((height, i) => (
                    <div key={i} className="flex-1 flex flex-col justify-end h-full max-w-[60px]">
                        <Skeleton 
                            className="w-full rounded-t-lg min-h-[40px]" 
                            style={{ height: `${height}%` }}
                        />
                    </div>
                ))}
            </div>
            
            {/* X-axis labels skeleton */}
            <div className="absolute bottom-2 left-0 right-0 flex justify-around px-8">
                {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-3 w-12" />
                ))}
            </div>
        </div>
    </motion.div>
);

const EmptyState = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="flex flex-col items-center justify-center h-[300px]"
    >
        <div className="relative">
            <div className="absolute inset-0 bg-primary/5 blur-2xl rounded-full" />
            <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl border-2 border-dashed border-primary/20 bg-background">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/10 to-transparent rounded-2xl" />
                <div className="relative">
                    <Clock className="w-7 h-7 text-primary/50" />
                </div>
            </div>
        </div>
        
        <div className="mt-6 text-center space-y-2">
            <div className="text-sm font-medium text-muted-foreground">No Call Time Data</div>
            <div className="text-xs text-muted-foreground/80 max-w-[250px]">
                There are currently no calls recorded in the selected time period to calculate average handling time.
            </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
            <motion.div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/10"
                whileHover={{ scale: 1.05, backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                transition={{ duration: 0.2 }}
            >
                <Ban className="w-3.5 h-3.5 text-primary/50" />
                <span className="text-xs font-medium text-primary/50">No Total</span>
            </motion.div>
            <motion.div 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/5 border border-primary/10"
                whileHover={{ scale: 1.05, backgroundColor: 'hsl(var(--primary) / 0.1)' }}
                transition={{ duration: 0.2 }}
            >
                <AlertCircle className="w-3.5 h-3.5 text-primary/50" />
                <span className="text-xs font-medium text-primary/50">No Inbound/Outbound</span>
            </motion.div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 opacity-[0.08]"
                style={{
                    background: `
                        linear-gradient(45deg, transparent 0%, hsl(var(--primary)) 100%),
                        linear-gradient(-45deg, transparent 0%, hsl(var(--primary)) 100%)
                    `,
                    maskImage: 'radial-gradient(circle at center, transparent 50%, black 150%)'
                }}
            />
        </div>
    </motion.div>
);

export const AverageHandlingTimeChart = ({ 
    className, 
    startDate, 
    endDate, 
    rubricId, 
    keyPath, 
    assistantId, 
    onError 
}: AverageHandlingTimeChartProps) => {
    const [data, setData] = useState<AverageHandlingTimeResponse['data'] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorState, setErrorState] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setErrorState(null);
            
            try {
                const params: any = { startDate, endDate };
                if (rubricId) params.rubricId = rubricId;
                if (keyPath) params.keyPath = keyPath;
                if (assistantId) params.assistantId = assistantId;
                
                const response = await getAverageHandlingTime(params);
                
                if (response.data) {
                    setData(response.data.data);
                } else if (response.error) {
                    throw new Error(response.error);
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to fetch data';
                setErrorState(message);
                onError?.(message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate, rubricId, keyPath, assistantId]);

    const hasNoData = !isLoading && !errorState && (!data || 
        (data.total.count === 0 && 
         data.inboundPhoneCalls.count === 0 && 
         data.outboundPhoneCalls.count === 0));

    // Prepare chart data
    const chartData = data ? [
        {
            name: 'Total',
            time: data.total.totalHandlingAverageTime,
            count: data.total.count,
            fill: 'var(--color-total)',
            icon: Phone,
            gradient: 'from-primary/20 via-primary/10 to-transparent',
        },
        {
            name: 'Inbound',
            time: data.inboundPhoneCalls.totalHandlingAverageTime,
            count: data.inboundPhoneCalls.count,
            fill: 'var(--color-inbound)',
            icon: PhoneIncoming,
            gradient: 'from-green-500/20 via-green-500/10 to-transparent',
        },
        {
            name: 'Outbound',
            time: data.outboundPhoneCalls.totalHandlingAverageTime,
            count: data.outboundPhoneCalls.count,
            fill: 'var(--color-outbound)',
            icon: PhoneOutgoing,
            gradient: 'from-blue-500/20 via-blue-500/10 to-transparent',
        },
    ] : [];

    if (errorState) {
        return (
            <Card className={cn("relative pt-8 overflow-visible shadow-sm", className)}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="relative flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-full blur-lg" />
                        <div className="relative bg-background/95 backdrop-blur-sm px-6 py-2 rounded-full border shadow-sm flex items-center gap-2">
                            <Timer className="h-4 w-4 text-primary/60" />
                            <span className="font-medium text-xs">Average Call Time</span>
                        </div>
                    </div>
                </div>
                <CardContent className="pt-4">
                    <div className="flex flex-col items-center justify-center h-[200px] text-center">
                        <AlertCircle className="h-8 w-8 text-destructive mb-2" />
                        <p className="text-sm text-destructive">{errorState}</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={cn("relative pt-8 overflow-visible shadow-sm h-full flex flex-col", className)}>
            {/* Floating Header Badge */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="relative flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-full blur-lg" />
                    <div className="relative bg-background/95 backdrop-blur-sm px-6 py-2 rounded-full border shadow-sm flex items-center gap-2">
                        <Timer className="h-4 w-4 text-primary/60" />
                        <span className="font-medium text-xs">Average Call Time</span>
                    </div>
                </div>
            </div>

            <CardContent className="px-5 pt-3 pb-5 flex-1 flex flex-col">
                <AnimatePresence mode="wait">
                    {isLoading ? (
                        <LoadingSkeleton />
                    ) : hasNoData ? (
                        <EmptyState />
                    ) : data ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="flex-1 flex flex-col space-y-4"
                        >
                            {/* Compact Stats Cards - Vertical Layout */}
                            <div className="grid grid-cols-3 gap-3">
                                {chartData.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <motion.div
                                            key={item.name}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.08, duration: 0.3 }}
                                            className={cn(
                                                "group relative rounded-lg border overflow-hidden",
                                                "bg-gradient-to-br",
                                                item.gradient,
                                                "hover:shadow-sm hover:border-primary/10 transition-all duration-200 cursor-default"
                                            )}
                                        >
                                            <div className="flex flex-col items-center text-center p-3 space-y-2">
                                                <div className={cn(
                                                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-md ring-1",
                                                    index === 0 ? "bg-primary/10 ring-primary/20" :
                                                    index === 1 ? "bg-green-500/10 ring-green-500/20" :
                                                    "bg-blue-500/10 ring-blue-500/20"
                                                )}>
                                                    <Icon className={cn(
                                                        "h-4 w-4",
                                                        index === 0 ? "text-primary" :
                                                        index === 1 ? "text-green-600" :
                                                        "text-blue-600"
                                                    )} />
                                                </div>
                                                <div className="w-full">
                                                    <p className="text-[10px] text-muted-foreground leading-none mb-1.5">
                                                        {item.name}
                                                    </p>
                                                    <p className="text-lg font-semibold leading-none mb-1">
                                                        {formatTime(item.time)}
                                                    </p>
                                                    <p className="text-[10px] text-muted-foreground/70 leading-none">
                                                        {item.count.toLocaleString()} {item.count === 1 ? 'call' : 'calls'}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* Enhanced Bar Chart with Background */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="relative rounded-lg border bg-gradient-to-br from-muted/30 via-background to-background px-4 pt-4 pb-2 flex-1 flex flex-col min-h-0"
                            >
                                {/* Subtle Background Pattern */}
                                <div className="absolute inset-0 opacity-[0.03]" style={{
                                    backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
                                    backgroundSize: '24px 24px'
                                }} />
                                
                                <div className="relative flex-1 min-h-0">
                                    <ChartContainer config={chartConfig} className="h-full w-full">
                                        <BarChart 
                                            accessibilityLayer
                                            data={chartData} 
                                            margin={{ top: 20, right: 10, bottom: 5, left: 10 }}
                                        >
                                            <defs>
                                                <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                                                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                                                </linearGradient>
                                                <linearGradient id="inboundGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.8} />
                                                    <stop offset="100%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.3} />
                                                </linearGradient>
                                                <linearGradient id="outboundGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.8} />
                                                    <stop offset="100%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.3} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid 
                                                strokeDasharray="3 3" 
                                                vertical={false}
                                                stroke="hsl(var(--border))"
                                                strokeOpacity={0.3}
                                            />
                                            <XAxis
                                                dataKey="name"
                                                tickLine={false}
                                                axisLine={{
                                                    stroke: 'hsl(var(--border))',
                                                    strokeWidth: 1,
                                                    strokeOpacity: 0.3
                                                }}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                                tickMargin={8}
                                            />
                                            <YAxis
                                                tickLine={false}
                                                axisLine={{
                                                    stroke: 'hsl(var(--border))',
                                                    strokeWidth: 1,
                                                    strokeOpacity: 0.3
                                                }}
                                                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                                tickFormatter={(value) => formatTime(value)}
                                                tickMargin={8}
                                                width={50}
                                            />
                                            <ChartTooltip
                                                cursor={{
                                                    fill: 'hsl(var(--muted))',
                                                    opacity: 0.3,
                                                    radius: 4
                                                }}
                                                content={({ active, payload }) => {
                                                    if (!active || !payload || !payload.length) return null;
                                                    
                                                    const data = payload[0].payload;
                                                    const Icon = data.icon;
                                                    
                                                    return (
                                                        <div className="rounded-lg border-2 bg-background shadow-lg overflow-hidden min-w-[150px]">
                                                            {/* Edge-to-edge header */}
                                                            <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 border-b">
                                                                <Icon className="h-4 w-4 text-primary" />
                                                                <span className="font-semibold text-sm">{data.name}</span>
                                                            </div>
                                                            
                                                            {/* Content with padding */}
                                                            <div className="px-3 py-2.5 space-y-2">
                                                                <div className="flex items-center justify-between gap-4">
                                                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                        <Timer className="h-3 w-3" />
                                                                        Avg. Time:
                                                                    </span>
                                                                    <span className="font-bold text-sm">
                                                                        {formatTime(data.time)}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center justify-between gap-4">
                                                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                        <PhoneOutgoing className="h-3 w-3" />
                                                                        Calls:
                                                                    </span>
                                                                    <span className="text-xs font-semibold">
                                                                        {data.count}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <ChartLegend content={<ChartLegendContent className="text-xs" />} />
                                            <Bar
                                                dataKey="time"
                                                radius={[6, 6, 4, 4]}
                                                maxBarSize={60}
                                                animationDuration={800}
                                                animationEasing="ease-out"
                                            >
                                                {chartData.map((_, index) => (
                                                    <Cell 
                                                        key={`cell-${index}`} 
                                                        fill={
                                                            index === 0 ? 'url(#totalGradient)' :
                                                            index === 1 ? 'url(#inboundGradient)' :
                                                            'url(#outboundGradient)'
                                                        }
                                                        style={{
                                                            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                                                            transition: 'all 0.2s ease',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ChartContainer>
                                </div>
                            </motion.div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </CardContent>
        </Card>
    );
};
